import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { PoundSterling, SlidersHorizontal, Sparkles, Mail, DraftingCompass, Newspaper, ShieldAlert } from "lucide-react";
import { AiSettings } from "./AiSettings";
import { SettingsEditor } from "./SettingsEditor";
import { PricingPanel } from "./PricingPanel";
import { GmailConnect } from "./GmailConnect";
import { EmailTemplatesEditor } from "./EmailTemplatesEditor";
import { BuildingPlanSettings } from "./BuildingPlanSettings";
import { BlogCachePanel } from "./BlogCachePanel";
import { SecurityPanel } from "./SecurityPanel";
import { PRICING_KEYS } from "../../lib/pricingKeys";

const SUBTABS = [
  { key: "pricing", label: "Pricing", Icon: PoundSterling },
  { key: "business", label: "Business & booking", Icon: SlidersHorizontal },
  { key: "email", label: "Email", Icon: Mail },
  { key: "ai", label: "AI", Icon: Sparkles },
  { key: "content", label: "Content", Icon: Newspaper },
  { key: "plan", label: "Building plan", Icon: DraftingCompass, adminOnly: true },
  { key: "security", label: "Security", Icon: ShieldAlert, seniorOnly: true },
];

export function SettingsTab({ settings, setSettings, isAdminLevel, isSenior }) {
  const [sub, setSub] = useState("pricing");
  const reloadSettings = () => api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});

  // Strip quote-item prices/titles — those are owned by the Pricing panel and must
  // not be overwritten by stale values carried in the SettingsEditor snapshot.
  const saveSettings = async (s) => {
    const payload = { ...s };
    PRICING_KEYS.forEach((k) => delete payload[k]);
    delete payload.price_titles;
    delete payload.custom_addons;
    const r = await api.put("/admin/settings", payload);
    setSettings(r.data);
    toast.success("Saved");
  };

  const tabs = SUBTABS.filter((t) => (!t.adminOnly || isAdminLevel) && (!t.seniorOnly || isSenior));

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E5E7EB] pb-4" data-testid="settings-subtabs">
        {tabs.map(({ key, label, Icon }) => (
          <button key={key} data-testid={`settings-subtab-${key}`} onClick={() => setSub(key)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors
              ${sub === key ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#12263A]"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {sub === "pricing" && (
        <div data-testid="settings-pricing">
          <PricingPanel onPricesSaved={reloadSettings} />
        </div>
      )}

      {sub === "business" && <SettingsEditor settings={settings} onSave={saveSettings} />}

      {sub === "ai" && (
        <AiSettings onApply={async (changes) => { const r = await api.put("/admin/settings", changes); setSettings(r.data); toast.success("Settings updated"); }} />
      )}

      {sub === "email" && (
        <>
          <div data-testid="settings-connected-email">
            <h2 className="text-xl font-black tracking-tight mb-1">Connected email</h2>
            <p className="text-sm text-[#6B7280] mb-5">Link your Gmail to read &amp; reply to customer emails from the Email tab.</p>
            <GmailConnect />
          </div>
          <div className="mt-12 pt-8 border-t border-[#E5E7EB]" data-testid="settings-email-templates">
            <h2 className="text-xl font-black tracking-tight mb-1">Email templates</h2>
            <p className="text-sm text-[#6B7280] mb-5">Edit the wording &amp; "from" address of every automated email (quotes, welcome, reminders, complaints).</p>
            <EmailTemplatesEditor canEdit={isAdminLevel} />
          </div>
        </>
      )}

      {sub === "content" && <BlogCachePanel />}

      {sub === "plan" && isAdminLevel && <BuildingPlanSettings settings={settings} setSettings={setSettings} />}

      {sub === "security" && isSenior && <SecurityPanel />}
    </>
  );
}
